import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentChartBarIcon,
  BellIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ClockIcon,
  ChartBarIcon,
  FunnelIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Reports = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dateRange, setDateRange] = useState('year');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState('all');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Dashboard Data
  const [stats, setStats] = useState({
    totalPartners: 0,
    activePartners: 0,
    totalInvoices: 0,
    draftInvoices: 0,
    sentInvoices: 0,
    partialInvoices: 0,
    paidInvoices: 0,
    overdueInvoices: 0,
    totalRevenue: 0,
    totalPaymentsReceived: 0,
    totalOutstanding: 0,
    partialPayments: 0,
    totalInvoiceAmount: 0
  });

  // Analytics Data
  const [revenueData, setRevenueData] = useState([]);
  const [partnerData, setPartnerData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [overdueData, setOverdueData] = useState([]);
  
  // Overdue Alerts
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [alertSettings, setAlertSettings] = useState({
    enabled: true,
    earlyWarning: 7,
    moderateAlert: 15,
    criticalAlert: 30
  });

  // Companies and Partners for filters
  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    fetchCompanies();
    fetchPartners();
  }, [dateRange, selectedCompany, selectedPartner]);

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      console.log('JWT Token:', token ? token.substring(0, 20) + '...' : 'No token found');
      
      const params = new URLSearchParams({
        dateRange,
        ...(selectedCompany !== 'all' && { companyId: selectedCompany }),
        ...(selectedPartner !== 'all' && { partnerId: selectedPartner })
      });

      // Fetch dashboard stats
      const statsResponse = await fetch(`/api/finance/dashboard/stats?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        console.log('Dashboard stats received:', statsData);
        console.log('totalInvoiceAmount value:', statsData.totalInvoiceAmount, 'type:', typeof statsData.totalInvoiceAmount);
        setStats(statsData);
      } else {
        console.error('Stats response not ok:', statsResponse.status, statsResponse.statusText);
        if (statsResponse.status === 401) {
          console.error('Authentication failed - please log in again');
          setMessage({ type: 'error', text: t('reports.authenticationFailed') });
        }
      }

      // Fetch analytics data
      const analyticsResponse = await fetch(`/api/finance/analytics?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setRevenueData(analyticsData.revenueData || []);
        setPartnerData(analyticsData.partnerData || []);
        setStatusData(analyticsData.statusData || []);
      }

      // Fetch overdue invoices
      const overdueResponse = await fetch('/api/finance/overdue-invoices', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (overdueResponse.ok) {
        const overdueData = await overdueResponse.json();
        setOverdueInvoices(overdueData.invoices || []);
      }

    } catch (error) {
      console.error('Error fetching reports data:', error);
      setMessage({ type: 'error', text: t('reports.failedToLoadReportsData') });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch('/api/finance/companies', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch('/api/finance/partners?activeOnly=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || data || []);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const generateReport = async (reportType) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      const params = new URLSearchParams({
        reportType,
        dateRange,
        ...(selectedCompany !== 'all' && { companyId: selectedCompany }),
        ...(selectedPartner !== 'all' && { partnerId: selectedPartner })
      });

      const response = await fetch(`/api/finance/reports/generate?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        const blob = await response.blob();
        console.log('Blob received:', {
          size: blob.size,
          type: blob.type
        });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        console.log('Download initiated for:', a.download);
        setMessage({ type: 'success', text: t('reports.reportGeneratedSuccessfully', { type: reportType }) });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || t('reports.failedToGenerateReport'));
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setMessage({ type: 'error', text: error.message || t('reports.failedToGenerateReport') });
    } finally {
      setLoading(false);
    }
  };

  const sendOverdueAlert = async (invoiceId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/overdue-alerts/${invoiceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('reports.overdueAlertSentSuccessfully') });
        fetchDashboardData(); // Refresh data
      } else {
        throw new Error(t('reports.failedToSendAlert'));
      }
    } catch (error) {
      console.error('Error sending overdue alert:', error);
      setMessage({ type: 'error', text: t('reports.failedToSendOverdueAlert') });
    }
  };

  const formatCurrency = (amount, currency = 'BGN') => {
    console.log('formatCurrency called with:', { amount, currency, type: typeof amount });
    try {
      const result = new Intl.NumberFormat('bg-BG', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
      console.log('formatCurrency result:', result);
      return result;
    } catch (error) {
      console.error('formatCurrency error:', error);
      return '0,00 лв';
    }
  };

  // Helper function to convert BGN to EUR
  const convertBGNtoEUR = (bgnAmount) => {
    // Using approximate rate: 1 EUR ≈ 1.95583 BGN
    return bgnAmount / 1.95583;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  const getDaysOverdue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getOverdueSeverity = (days) => {
    if (days <= alertSettings.earlyWarning) return 'low';
    if (days <= alertSettings.moderateAlert) return 'medium';
    return 'high';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return '#6B7280'; // gray
      case 'Paid': return '#10B981'; // green
      case 'Partial': return '#F59E0B'; // yellow
      case 'Overdue': return '#EF4444'; // red
      case 'Sent': return '#3B82F6'; // blue
      default: return '#8884D8'; // default purple
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('reports.loadingReports')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto">
              {/* Hero Section */}
        <div className="flex items-center justify-between mb-6 px-4 sm:px-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.financialReportsAndAnalytics')}</h1>
          <p className="text-gray-600">{t('reports.comprehensiveFinancialInsights')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => generateReport('revenue')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            <span>{t('reports.exportRevenueReport')}</span>
          </button>
          <button
            onClick={() => generateReport('overdue')}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            <span>{t('reports.exportOverdueReport')}</span>
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <span>{message.text}</span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports.dateRange')}</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="week">{t('reports.lastWeek')}</option>
              <option value="month">{t('reports.lastMonth')}</option>
              <option value="quarter">{t('reports.lastQuarter')}</option>
              <option value="year">{t('reports.lastYear')}</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports.company')}</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('reports.allCompanies')}</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports.partner')}</label>
            <select
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('reports.allPartners')}</option>
              {partners.map((partner) => (
                <option key={partner._id} value={partner._id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchDashboardData}
              className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <FunnelIcon className="h-4 w-4" />
              <span>{t('reports.applyFilters')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <ChartBarIcon className="h-4 w-4" />
                <span>{t('reports.analyticsDashboard')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('revenue')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'revenue'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <CurrencyDollarIcon className="h-4 w-4" />
                <span>{t('reports.revenueReports')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('overdue')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overdue'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>{t('reports.overdueAlerts')}</span>
                {overdueInvoices.length > 0 && (
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                    {overdueInvoices.length}
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <CogIcon className="h-4 w-4" />
                <span>{t('reports.alertSettings')}</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Analytics Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">{t('reports.totalRevenue')}</p>
                      <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                    </div>
                    <CurrencyDollarIcon className="h-8 w-8 text-blue-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">{t('reports.paymentsReceived')}</p>
                      <p className="text-2xl font-bold">{formatCurrency(stats.totalPaymentsReceived)}</p>
                    </div>
                    <CheckCircleIcon className="h-8 w-8 text-green-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">{t('reports.outstanding')}</p>
                      <p className="text-2xl font-bold">{formatCurrency(stats.totalOutstanding)}</p>
                    </div>
                    <ExclamationTriangleIcon className="h-8 w-8 text-orange-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">{t('reports.totalInvoiceAmount')}</p>
                      <p className="text-2xl font-bold">
                        {(() => {
                          const amount = stats.totalInvoiceAmount || 0;
                          console.log('Formatting BGN amount:', amount, 'type:', typeof amount);
                          return formatCurrency(amount, 'BGN');
                        })()}
                      </p>
                      <p className="text-sm text-purple-200">
                        {(() => {
                          const amount = stats.totalInvoiceAmount || 0;
                          const eurAmount = convertBGNtoEUR(amount);
                          console.log('Formatting EUR amount:', eurAmount, 'type:', typeof eurAmount);
                          return formatCurrency(eurAmount, 'EUR');
                        })()}
                      </p>
                    </div>
                    <DocumentChartBarIcon className="h-8 w-8 text-purple-200" />
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Trend */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.revenueTrend')}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Invoice Status Distribution */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.invoiceStatusDistribution')}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.name)} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => value} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Partner Performance */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.topPartnersByRevenue')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={partnerData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="revenue" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Revenue Reports Tab */}
          {activeTab === 'revenue' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.monthlyRevenueReport')}</h3>
                  <p className="text-gray-600 mb-4">{t('reports.comprehensiveMonthlyRevenueAnalysis')}</p>
                  <button
                    onClick={() => generateReport('monthly-revenue')}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    {t('reports.generateReport')}
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.partnerPerformanceReport')}</h3>
                  <p className="text-gray-600 mb-4">{t('reports.detailedAnalysisOfPartnerPayment')}</p>
                  <button
                    onClick={() => generateReport('partner-performance')}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200"
                  >
                    {t('reports.generateReport')}
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.collectionEfficiencyReport')}</h3>
                  <p className="text-gray-600 mb-4">{t('reports.analysisOfPaymentCollectionRates')}</p>
                  <button
                    onClick={() => generateReport('collection-efficiency')}
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-200"
                  >
                    {t('reports.generateReport')}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.revenueSummary')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.totalRevenue)}</p>
                    <p className="text-sm text-gray-600">{t('reports.totalRevenue')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalPaymentsReceived)}</p>
                    <p className="text-sm text-gray-600">{t('reports.paymentsReceived')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.totalOutstanding)}</p>
                    <p className="text-sm text-gray-600">{t('reports.outstanding')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.averageInvoice)}</p>
                    <p className="text-sm text-gray-600">{t('reports.averageInvoice')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Overdue Alerts Tab */}
          {activeTab === 'overdue' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">{t('reports.overdueInvoices')}</h3>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {overdueInvoices.length} {t('reports.overdueInvoices')}
                  </span>
                  <button
                    onClick={() => generateReport('overdue')}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    <span>{t('reports.exportReport')}</span>
                  </button>
                </div>
              </div>

              {overdueInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">{t('reports.noOverdueInvoicesFound')}</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.invoice')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.partner')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.amount')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.dueDate')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.daysOverdue')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.severity')}
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {overdueInvoices.map((invoice) => {
                          const daysOverdue = getDaysOverdue(invoice.dueDate);
                          const severity = getOverdueSeverity(daysOverdue);
                          
                          return (
                            <tr key={invoice._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {invoice.invoiceNumber}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {invoice.partnerId?.name || t('reports.unknownPartner')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {formatCurrency(invoice.totalAmount)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {formatDate(invoice.dueDate)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {daysOverdue} {t('reports.days')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  severity === 'high' ? 'bg-red-100 text-red-800' :
                                  severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {t(`reports.severity${severity.charAt(0).toUpperCase() + severity.slice(1)}`)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-2">
                                  <button
                                    onClick={() => sendOverdueAlert(invoice._id)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title={t('reports.sendAlert')}
                                  >
                                    <BellIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => window.location.href = `/admin/dashboard/invoices/${invoice._id}`}
                                    className="text-green-600 hover:text-green-900"
                                    title={t('reports.viewInvoice')}
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Alert Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reports.overdueAlertSettings')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('reports.enableOverdueAlerts')}</label>
                      <p className="text-sm text-gray-500">{t('reports.automaticallySendAlerts')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={alertSettings.enabled}
                        onChange={(e) => setAlertSettings({...alertSettings, enabled: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports.earlyWarning')} ({t('reports.days')})</label>
                      <input
                        type="number"
                        value={alertSettings.earlyWarning}
                        onChange={(e) => setAlertSettings({...alertSettings, earlyWarning: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                        max="30"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports.moderateAlert')} ({t('reports.days')})</label>
                      <input
                        type="number"
                        value={alertSettings.moderateAlert}
                        onChange={(e) => setAlertSettings({...alertSettings, moderateAlert: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                        max="60"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('reports.criticalAlert')} ({t('reports.days')})</label>
                      <input
                        type="number"
                        value={alertSettings.criticalAlert}
                        onChange={(e) => setAlertSettings({...alertSettings, criticalAlert: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                        max="90"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        // Save alert settings
                        setMessage({ type: 'success', text: t('reports.alertSettingsSavedSuccessfully') });
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      {t('reports.saveSettings')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
                     )}
         </div>
       </div>
     </div>
     </div>
   );
 };

export default Reports; 